import { useState, useCallback } from 'react'
import { apiClient } from '../utils/api'

interface GeneratedImage {
  id: string
  url: string
  prompt: string
  model: string
  created_at: string
  cached?: boolean
  provider?: string
  type?: string
}

interface GenerationProgress {
  status: string
  message?: string
  percentage?: number
}

export function useImageGeneration() {
  const [isGenerating, setIsGenerating] = useState(false)
  const [progress, setProgress] = useState<GenerationProgress | null>(null)
  const [currentImage, setCurrentImage] = useState<GeneratedImage | null>(null)
  const [currentBatchImages, setCurrentBatchImages] = useState<GeneratedImage[]>([])
  const [generatedImages, setGeneratedImages] = useState<GeneratedImage[]>([])
  const [error, setError] = useState<string | null>(null)

  const generateImage = useCallback(async (prompt: string, model?: string) => {
    setIsGenerating(true)
    setError(null)
    setProgress({ status: 'starting', message: '初始化生成...', percentage: 0 })

    try {
      setProgress({ status: 'generating', message: '正在生成图像...', percentage: 25 })

      const result = await apiClient.generateImage(prompt, undefined, model)

      setProgress({ status: 'processing', message: '处理生成结果...', percentage: 75 })

      setProgress({ status: 'completed', message: '生成完成！', percentage: 100 })

      const newImage: GeneratedImage = {
        ...result,
        type: 'image'
      }

      setCurrentImage(newImage)
      setCurrentBatchImages([newImage])
      setGeneratedImages(prev => [newImage, ...prev.slice(0, 19)]) // Keep last 20 images

      // Clear progress after a short delay
      setTimeout(() => {
        setProgress(null)
      }, 2000)

    } catch (err) {
      console.error('Generation error:', err)
      setError(err instanceof Error ? err.message : '生成失败，请重试')
      setProgress(null)
    } finally {
      setIsGenerating(false)
    }
  }, [])

  const generateBatchImages = useCallback(async (prompt: string, count: number, model?: string) => {
    setIsGenerating(true)
    setError(null)
    setCurrentBatchImages([])
    setProgress({ status: 'starting', message: `准备生成 ${count} 张图像...`, percentage: 0 })

    try {
      // 并发生成多张图片，每张使用不同的随机 seed
      const promises = Array.from({ length: count }, async (_, index) => {
        try {
          setProgress({
            status: 'generating',
            message: `正在生成第 ${index + 1}/${count} 张图像...`,
            percentage: Math.floor(((index + 0.5) / count) * 100)
          })

          // 为每个请求生成不同的随机 seed
          const seed = Math.floor(Math.random() * 1000000)
          const result = await apiClient.generateImage(prompt, seed, model)

          const newImage: GeneratedImage = {
            ...result,
            type: 'image'
          }

          // 实时更新当前批次图片
          setCurrentBatchImages(prev => [...prev, newImage])

          return newImage
        } catch (err) {
          console.error(`Failed to generate image ${index + 1}:`, err)
          return null
        }
      })

      const results = await Promise.all(promises)
      const successfulImages = results.filter((img): img is GeneratedImage => img !== null)

      if (successfulImages.length === 0) {
        throw new Error('所有图像生成失败')
      }

      setProgress({ status: 'completed', message: `成功生成 ${successfulImages.length}/${count} 张图像！`, percentage: 100 })

      // 更新最新单图（取第一张）
      setCurrentImage(successfulImages[0])
      // 更新历史记录
      setGeneratedImages(prev => [...successfulImages, ...prev.slice(0, 20 - successfulImages.length)])

      // Clear progress after a short delay
      setTimeout(() => {
        setProgress(null)
      }, 2000)

      if (successfulImages.length < count) {
        setError(`部分图像生成失败：成功 ${successfulImages.length}/${count}`)
      }

    } catch (err) {
      console.error('Batch generation error:', err)
      setError(err instanceof Error ? err.message : '批量生成失败，请重试')
      setProgress(null)
    } finally {
      setIsGenerating(false)
    }
  }, [])

  const clearError = useCallback(() => {
    setError(null)
  }, [])

  return {
    generateImage,
    generateBatchImages,
    isGenerating,
    progress,
    currentImage,
    currentBatchImages,
    generatedImages,
    error,
    clearError,
  }
}
